import React, { useCallback, useMemo, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "@/contexts/AuthContext";
import { readSidebarCollapsed, writeSidebarCollapsed } from "@/lib/uiPreferences";
import { cn } from "@/lib/utils";

export default function AppShell() {
  const { isAuthenticated, hydrated } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);

  const toggle = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);

  // Stable Navigate state — avoids inline-object re-renders.
  const navigateState = useMemo(() => ({ from: location }), [location]);

  if (!hydrated) {
    return (
      <div className="min-h-screen w-full bg-canvas flex items-center justify-center" />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={navigateState} replace />;
  }

  return (
    <div className="min-h-screen w-full bg-canvas" data-testid="app-shell">
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div
        className={cn(
          "flex flex-col min-h-screen transition-[margin] duration-200",
          collapsed ? "ml-[64px]" : "ml-[260px]",
        )}
      >
        <Header />
        <main className="flex-1 min-w-0" data-testid="app-main">
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}
